import { EllipsisIcon } from 'components/icons';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Button } from 'components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Tooltip } from 'components/ui/tooltip';
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    useCreateHomeWidgetMutation,
    useDeleteHomeWidgetMutation,
    useGetHomeQuery,
    useSaveHomeLayoutMutation,
} from 'store/api/home';
import { useGetWidgetCatalogueQuery } from 'store/api/homeWidgetFigures';
import { SHARE_WITH_FIRM_PERMISSIONS, type SharedHomeWidget } from 'types/homeWidgetSharing';
import { HOME_WIDGET_NAME_MAX, type HomeWidgetSize } from 'types/homeWidgets';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { WidgetCreator } from './creator/WidgetCreator';
import { addEntry, addsGap, entryKey, removeEntry, toLayoutSave } from './grid/layout';
import { useWidgetFigure } from './kpi/useWidgetFigure';
import { widgetLinks, widgetListLabel } from './kpi/widgetLink';
import { ShareWidget } from './share/ShareWidget';

type Props = {
    widget: SharedHomeWidget;
    /**
     * The widget's size on Home, for the menu of its card there: it then also opens the widget's list, copies the
     * widget and takes it off Home. Without it the menu is that of a widget which is not on Home, to change,
     * share or delete it.
     */
    size?: HomeWidgetSize;
};

const ITEM_CLASS = 'h-[34px] px-2.5';

/**
 * What can be done with a widget somebody built, behind the ⋯ of its card. Changing and deleting are for whoever
 * may edit it, sharing it with the firm for those of them who hold that privilege; the rest is for anyone who has
 * it on their Home. A widget added from the firm gallery is its author's to change: its viewer copies it to get
 * one of their own. Each action is saved as it is taken: the menu is out of reach while Home is being arranged.
 */
export function WidgetMenu({ widget, size }: Props) {
    const [isEditing, setIsEditing] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const { data: home } = useGetHomeQuery();
    const [createWidget] = useCreateHomeWidgetMutation();
    const [saveLayout] = useSaveHomeLayoutMutation();
    const [deleteWidget] = useDeleteHomeWidgetMutation();
    const canShare = useHasPermissions(SHARE_WITH_FIRM_PERMISSIONS) && widget.canEdit;
    const onHome = size !== undefined && home !== undefined;
    // A copy needs a place: on a full Home there is none to give it.
    const canDuplicate = onHome && home.own.length < home.maxOwnEntries;
    // A copy goes last at the same size, and is not offered where that would leave a gap on Home.
    const copyGaps = canDuplicate && addsGap(home.own, addEntry(home.own, { kind: 'WIDGET', widget }, size));
    const gapId = useId();

    if (!onHome && !widget.canEdit) {
        return null;
    }

    const duplicate = async () => {
        if (!canDuplicate) return;
        try {
            const copy = await createWidget({
                name: t('home.widgets.duplicate-name', { name: widget.name }).slice(0, HOME_WIDGET_NAME_MAX),
                definition: widget.definition,
            }).unwrap();
            try {
                await saveLayout(toLayoutSave([...home.own, { kind: 'WIDGET', widget: copy, size }])).unwrap();
            } catch {
                // A copy that found no place on Home is taken back, so trying again leaves none behind.
                void deleteWidget(copy.id);
            }
        } catch {
            // The failure is reported where every request's is.
        }
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger
                    render={
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t('home.widgets.menu.label', { name: widget.name })}
                            data-tour={onHome ? 'home-widget-menu' : undefined}
                            className="text-muted-foreground -me-1.5 size-7"
                        />
                    }
                >
                    <EllipsisIcon />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                    {onHome ? <OpenInList widget={widget} /> : null}
                    {widget.canEdit ? (
                        <DropdownMenuItem className={ITEM_CLASS} onClick={() => setIsEditing(true)}>
                            {t('edit')}
                        </DropdownMenuItem>
                    ) : null}
                    {copyGaps ? (
                        // A disabled item takes no pointer events, so the tooltip hangs off a wrapper.
                        <Tooltip content={t('home.widgets.leaves-gap')} side="left">
                            <div>
                                <DropdownMenuItem disabled aria-describedby={gapId} className={ITEM_CLASS}>
                                    {t(
                                        widget.canEdit
                                            ? 'home.widgets.menu.duplicate'
                                            : 'home.widgets.menu.duplicate-shared'
                                    )}
                                </DropdownMenuItem>
                                <span id={gapId} hidden>
                                    {t('home.widgets.leaves-gap')}
                                </span>
                            </div>
                        </Tooltip>
                    ) : canDuplicate ? (
                        <DropdownMenuItem className={ITEM_CLASS} onClick={duplicate}>
                            {t(widget.canEdit ? 'home.widgets.menu.duplicate' : 'home.widgets.menu.duplicate-shared')}
                        </DropdownMenuItem>
                    ) : null}
                    {canShare ? (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className={ITEM_CLASS} onClick={() => setIsSharing(true)}>
                                {t('home.widgets.menu.share')}
                            </DropdownMenuItem>
                        </>
                    ) : null}
                    {onHome || canShare ? <DropdownMenuSeparator /> : null}
                    {onHome ? (
                        <DropdownMenuItem
                            className={ITEM_CLASS}
                            onClick={() =>
                                saveLayout(toLayoutSave(removeEntry(home.own, entryKey({ kind: 'WIDGET', widget }))))
                            }
                        >
                            {t('home.widgets.remove')}
                        </DropdownMenuItem>
                    ) : null}
                    {widget.canEdit ? (
                        <DropdownMenuItem
                            variant="destructive"
                            className={ITEM_CLASS}
                            onClick={() => setIsDeleting(true)}
                        >
                            {t('delete')}
                        </DropdownMenuItem>
                    ) : null}
                    {onHome && !widget.canEdit ? (
                        <p className="border-neutral-tint text-muted-foreground text-caption mt-1 border-t px-2.5 pt-2 pb-1">
                            {t('home.widgets.menu.published-by', { name: widget.owner.name })}
                        </p>
                    ) : null}
                </DropdownMenuContent>
            </DropdownMenu>
            <WidgetCreator open={isEditing} onClose={() => setIsEditing(false)} widget={widget} size={size} />
            {canShare ? <ShareWidget widget={widget} open={isSharing} onClose={() => setIsSharing(false)} /> : null}
            <DeleteWidgetDialog widget={widget} open={isDeleting} onClose={() => setIsDeleting(false)} />
        </>
    );
}

/** What deleting a shared widget takes with it, for the question that precedes it; nothing for a private one. */
function sharedLine({ sharing }: SharedHomeWidget): string | null {
    const count = String(sharing?.recipientCount ?? 0);
    if (sharing?.inGallery && sharing.onHomes) return t('home.widgets.delete.shared', { count });
    if (sharing?.inGallery) return t('home.widgets.delete.shared-gallery');
    return sharing?.onHomes ? t('home.widgets.delete.shared-homes', { count }) : null;
}

type DeleteProps = {
    widget: SharedHomeWidget;
    open: boolean;
    onClose: () => void;
};

/**
 * The question before a widget is deleted for good. A shared one leaves the gallery and every Home it is on with
 * it, so the question says where it is and for how many people.
 */
export function DeleteWidgetDialog({ widget, open, onClose }: DeleteProps) {
    const [deleteWidget, { isLoading }] = useDeleteHomeWidgetMutation();
    const shared = sharedLine(widget);

    const remove = async () => {
        try {
            await deleteWidget(widget.id).unwrap();
            onClose();
        } catch {
            // The failure is reported where every request's is; the question stays open.
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={(next) => !next && onClose()}>
            <AlertDialogContent className="data-[size=default]:sm:max-w-[440px]">
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('home.widgets.delete.title', { name: widget.name })}</AlertDialogTitle>
                    <AlertDialogDescription>{t('home.widgets.delete.body')}</AlertDialogDescription>
                    {shared ? (
                        <p className="bg-danger-tint text-danger-ink text-meta mt-1.5 justify-self-stretch rounded-md px-3 py-2.5 text-start">
                            {shared}
                        </p>
                    ) : null}
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                    <AlertDialogAction variant="destructive" disabled={isLoading} onClick={remove}>
                        {t('delete')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

/**
 * The list the widget's figure counts, opened on what it counted, whichever way the widget was built. It reads the
 * figure the card already has, and is absent while there is none to lead from: a figure still on its way, or one
 * closed to the viewer.
 */
function OpenInList({ widget }: { widget: SharedHomeWidget }) {
    const { definition } = widget;
    const { data: catalogue } = useGetWidgetCatalogueQuery();
    const { figure, view } = useWidgetFigure(definition, widget.id);
    const link = figure ? widgetLinks(catalogue, definition, figure.period, view)?.list : undefined;
    const list = widgetListLabel(definition);
    if (!link || !list) {
        return null;
    }
    return (
        <DropdownMenuItem className={ITEM_CLASS} render={<Link to={link} />}>
            {t('home.widgets.menu.open-in', { list: t(list) })}
        </DropdownMenuItem>
    );
}
